import { formatString } from '@common/utils/formatString';
import type { PaginationLinkType } from './Pagination/Pagination';
import { getSelectedLanguage } from '../../translate';
import { DropdownInput } from '../fields/Dropdown';
import { FormLabel } from './forms/FormLabel';
import { HorizontalFormGroup } from './forms/HorizontalFormGroup';
import { InputContainer } from './forms/InputContainer';
import { Pagination, PaginationItem, PaginationLink } from './Pagination/Pagination';

interface IListPagerProps {
    hasMoreItems?: boolean;
    isDisabled?: boolean;
    isForwardOnly?: boolean;
    itemsPerPage?: number;
    onChangeItemsPerPage?: (itemsPerPage: number) => Promise<void> | void;
    onChangePage: (page: number) => Promise<void> | void;
    page: number;
    showNoResultsText?: boolean;
    total: number;
}

/**
 * @deprecated Use Pagination instead
 */
export function ListPager({
    hasMoreItems = false,
    isDisabled = false,
    isForwardOnly = false,
    itemsPerPage = 20,
    onChangeItemsPerPage,
    onChangePage,
    page,
    showNoResultsText,
    total,
}: IListPagerProps) {
    if (!showNoResultsText && total === 0) {
        return null;
    }

    const translate = getSelectedLanguage();
    const totalPages = isForwardOnly ? 0 : Math.ceil(total / itemsPerPage);
    const firstItemOnPage = (page - 1) * itemsPerPage + 1;
    const showPagination = page > 1 || totalPages > 1 || (isForwardOnly && hasMoreItems);

    // When the pager is 'forward only', the total should contain the number of items on the current page.
    const lastItemOnPage = isForwardOnly
        ? (page - 1) * itemsPerPage + total
        : page === totalPages
          ? total
          : page * itemsPerPage;

    const radius = 3;
    const dropdownOptions = [10, 15, 20, 25, 50, 100];
    const dropdownSelection = dropdownOptions.includes(itemsPerPage) ? itemsPerPage : 20;

    return (
        <HorizontalFormGroup>
            {total === 0 && <FormLabel>{formatString(translate.common.NrOfResults, ['0'])}</FormLabel>}

            {total !== 0 && (
                <>
                    <FormLabel>
                        {isForwardOnly
                            ? formatString(translate.common.ResultRange, [`${firstItemOnPage}`, `${lastItemOnPage}`])
                            : formatString(translate.common.ResultRangeOfTotal, [
                                  `${firstItemOnPage}`,
                                  `${lastItemOnPage}`,
                                  `${total}`,
                              ])}
                    </FormLabel>

                    {onChangeItemsPerPage && (
                        <>
                            <InputContainer size="small">
                                <FormLabel>{translate.common.PerPage}</FormLabel>
                            </InputContainer>
                            <InputContainer size="medium">
                                <DropdownInput
                                    onChange={async (value: number | null) => {
                                        if (!value) {
                                            return;
                                        }
                                        onChangeItemsPerPage?.(value);
                                    }}
                                    selectOptions={dropdownOptions.map((option) => {
                                        return { text: `${option}`, value: option };
                                    })}
                                    value={dropdownSelection}
                                />
                            </InputContainer>
                        </>
                    )}

                    {showPagination && (
                        <InputContainer>
                            <Pagination position="end">{createPaginationItems()}</Pagination>
                        </InputContainer>
                    )}
                </>
            )}
        </HorizontalFormGroup>
    );

    function createPaginationItems() {
        const paginationItems: JSX.Element[] = [];

        // Always add first/prev/1 links.
        paginationItems.push(
            createPaginationItem({ key: 'first', toPage: 1, type: 'first' }),
            createPaginationItem({ key: 'prev', toPage: page - 1, type: 'prev' }),
            createPaginationItem({ key: '1', toPage: 1, type: 'pagenumber' })
        );

        // Only add first ellipsis when the radius' left edge has passed 2.
        if (page - radius > 2) {
            paginationItems.push(createPaginationItem({ key: 'pre-ellipsis', toPage: -1, type: 'ellipsis' }));
        }

        // Add all page links from the (capped) radius' left edge up to the second to last page link.
        const upperLimit = totalPages > 0 ? Math.min(totalPages - 1, page + radius) : hasMoreItems ? page + 1 : page;

        for (let i = Math.max(2, page - radius); i <= upperLimit; i++) {
            paginationItems.push(createPaginationItem({ key: String(i), toPage: i, type: 'pagenumber' }));
        }

        // Only add second ellipsis when the radius' right edge is before the second to last page.
        if (page + radius < totalPages - 1) {
            paginationItems.push(createPaginationItem({ key: 'post-ellipsis', toPage: -1, type: 'ellipsis' }));
        }

        // Add last page link.
        if (totalPages > 1 && totalPages >= page) {
            paginationItems.push(
                createPaginationItem({ key: String(totalPages), toPage: totalPages, type: 'pagenumber' })
            );
        }

        // Add next page link.
        paginationItems.push(createPaginationItem({ key: 'next', toPage: page + 1, type: 'next' }));

        // Add last page link when we know a total.
        if (totalPages > 1) {
            paginationItems.push(createPaginationItem({ key: 'last', toPage: totalPages, type: 'last' }));
        }

        return paginationItems;
    }

    function createPaginationItem({ key, toPage, type }: { key: string; toPage: number; type: PaginationLinkType }) {
        const isCurrentPage = toPage === page;
        const isInvalidToPage = toPage < 1 || (totalPages > 1 && toPage > totalPages);
        const isNextWithoutMore = type === 'next' && hasMoreItems === false;
        const isItemDisabled = isCurrentPage || isInvalidToPage || isNextWithoutMore || isDisabled;

        return (
            <PaginationItem
                isCurrentPage={type === 'pagenumber' && isCurrentPage}
                isDisabled={isItemDisabled}
                key={key}
            >
                <PaginationLink
                    onClick={onChangePage}
                    page={toPage}
                    type={type}
                />
            </PaginationItem>
        );
    }
}
